import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import AnalyticsHeader from './components/AnalyticsHeader';
import KPISummary from './components/KPISummary';
import RevenueAnalytics from './components/RevenueAnalytics';
import InvoiceBreakdown from './components/InvoiceBreakdown';
import ProjectProfitability from './components/ProjectProfitability';
import ClientPerformance from './components/ClientPerformance';
import SeasonalPatterns from './components/SeasonalPatterns';
import CashFlowProjections from './components/CashFlowProjections';
import ProjectCompletion from './components/ProjectCompletion';
import SubcontractorPerformance from './components/SubcontractorPerformance';
import CostAnalysis from './components/CostAnalysis';
import GeographicRevenue from './components/GeographicRevenue';
import FilterSidebar from './components/FilterSidebar';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission, FEATURES, getEffectiveRoleFromProfile } from '../../utils/rbac';
import Icon from '../../components/AppIcon';
import { analyticsService } from '../../services/analyticsService';

const Analytics = () => {
  const { userProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('yearly');
  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    projectTypes: [],
    clientSegments: [],
    regions: []
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [kpis, setKpis] = useState([]);
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [invoiceStatusSeries, setInvoiceStatusSeries] = useState([]);
  const [invoiceMonthlySeries, setInvoiceMonthlySeries] = useState([]);
  const [projectMetrics, setProjectMetrics] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        // Minimal real-data wiring for KPIs, revenue, invoices, project profitability
        const { data: { user } } = await (await import('../../lib/supabase')).supabase.auth.getUser();
        if (!user) {
          setIsLoading(false);
          return;
        }
        const analyticsData = await analyticsService.getAnalyticsData(user.id, {});
        const revenue = analyticsService.calculateRevenueAnalytics(analyticsData);
        const projects = analyticsService.calculateProjectProfitability(analyticsData);

        // KPIs
        const kpiComputed = [
          {
            title: 'Total Revenue',
            value: `$${(revenue.totalRevenue || 0).toLocaleString()}`,
            change: '',
            changeType: 'positive',
            icon: 'DollarSign',
            color: 'primary',
            description: 'paid invoices'
          },
          {
            title: 'Pending Revenue',
            value: `$${(revenue.pendingRevenue || 0).toLocaleString()}`,
            change: '',
            changeType: 'neutral',
            icon: 'FileText',
            color: 'construction',
            description: 'sent, unpaid'
          },
          {
            title: 'Overdue Revenue',
            value: `$${(revenue.overdueRevenue || 0).toLocaleString()}`,
            change: '',
            changeType: 'negative',
            icon: 'AlertTriangle',
            color: 'warning',
            description: 'past due'
          },
          {
            title: 'Active Projects',
            value: String(analyticsData.projects?.filter(p => p.status === 'active')?.length || 0),
            change: '',
            changeType: 'neutral',
            icon: 'Building2',
            color: 'success',
            description: 'currently active'
          }
        ];
        setKpis(kpiComputed);

        // Revenue series from monthlyRevenue map
        const revenueSeriesComputed = Object.entries(revenue.monthlyRevenue || {})
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([ym, total]) => ({ period: ym, revenue: total }));
        setRevenueSeries(revenueSeriesComputed);

        // Invoice status breakdown
        const paid = analyticsData.invoices?.filter(i => i.status === 'paid') || [];
        const sent = analyticsData.invoices?.filter(i => i.status === 'sent') || [];
        const overdue = analyticsData.invoices?.filter(i => i.status === 'overdue') || [];
        const totalAmount = (arr) => arr.reduce((s, i) => s + (i.total_amount || 0), 0);
        const totalAll = totalAmount(paid) + totalAmount(sent) + totalAmount(overdue);
        const pct = (amt) => totalAll > 0 ? Math.round((amt / totalAll) * 100) : 0;
        const statusSeries = [
          { name: 'Paid', value: pct(totalAmount(paid)), amount: totalAmount(paid), color: '#22c55e' },
          { name: 'Pending', value: pct(totalAmount(sent)), amount: totalAmount(sent), color: '#f59e0b' },
          { name: 'Overdue', value: pct(totalAmount(overdue)), amount: totalAmount(overdue), color: '#ef4444' }
        ];
        setInvoiceStatusSeries(statusSeries);

        // Monthly invoices aggregation by created_at month
        const monthlyAgg = {};
        (analyticsData.invoices || []).forEach(inv => {
          const m = (inv.created_at || '').substring(0, 7) || 'Unknown';
          if (!monthlyAgg[m]) monthlyAgg[m] = { month: m, paid: 0, pending: 0, overdue: 0 };
          const amt = inv.total_amount || 0;
          if (inv.status === 'paid') monthlyAgg[m].paid += amt;
          else if (inv.status === 'sent') monthlyAgg[m].pending += amt;
          else if (inv.status === 'overdue') monthlyAgg[m].overdue += amt;
        });
        const monthlySeries = Object.values(monthlyAgg).sort((a, b) => String(a.month).localeCompare(String(b.month)));
        setInvoiceMonthlySeries(monthlySeries);

        // Project profitability dataset
        setProjectMetrics(projects.projects || []);
      } catch (e) {
        console.error('Failed loading analytics:', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const effectiveRole = getEffectiveRoleFromProfile(userProfile);
  if (!hasPermission(effectiveRole, FEATURES.VIEW_ANALYTICS)) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <Breadcrumb />
          <main className="px-4 lg:px-6 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Lock" size={48} className="text-destructive mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
                <p className="text-muted-foreground">You do not have permission to view this page. Please upgrade to an Enterprise plan.</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <Breadcrumb />
          <main className="px-4 lg:px-6 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading analytics...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Analytics - BuildLedger</title>
        <meta name="description" content="Comprehensive business intelligence and analytics for construction operations" />
      </Helmet>
      <Header />
      <div className="pt-16">
        <Breadcrumb />
        
        <main className="px-4 lg:px-6 py-8">
          {/* Analytics Header with Controls */}
          <AnalyticsHeader 
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            onToggleSidebar={toggleSidebar}
          />

          <div className="flex gap-6">
            {/* Filter Sidebar */}
            <FilterSidebar 
              isOpen={isSidebarOpen}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClose={() => setIsSidebarOpen(false)}
            />

            {/* Main Content */}
            <div className={`flex-1 space-y-8 ${isSidebarOpen ? 'lg:ml-80' : ''} transition-all duration-300`}>
              {/* KPI Summary Cards */}
              <KPISummary dateRange={dateRange} filters={filters} kpiData={kpis} />

              {/* Revenue Analytics */}
              <RevenueAnalytics dateRange={dateRange} filters={filters} data={revenueSeries} />

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <InvoiceBreakdown dateRange={dateRange} filters={filters} statusData={invoiceStatusSeries} monthlyData={invoiceMonthlySeries} />
                <ProjectProfitability dateRange={dateRange} filters={filters} projectsMetrics={projectMetrics} />
              </div>

              {/* Client Performance */}
              <ClientPerformance dateRange={dateRange} filters={filters} />

              {/* Seasonal and Cash Flow */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <SeasonalPatterns dateRange={dateRange} filters={filters} />
                <CashFlowProjections dateRange={dateRange} filters={filters} />
              </div>

              {/* Project Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <ProjectCompletion dateRange={dateRange} filters={filters} />
                <SubcontractorPerformance dateRange={dateRange} filters={filters} />
              </div>

              {/* Cost Analysis and Geographic Revenue */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <CostAnalysis dateRange={dateRange} filters={filters} />
                <GeographicRevenue dateRange={dateRange} filters={filters} />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center py-8 border-t border-border mt-12">
            <p className="text-sm text-muted-foreground">
              BuildLedger Analytics - Data-Driven Construction Management
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Last updated: {new Date()?.toLocaleString()}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Analytics;